import { useState } from "react";
import type { Client, Entry } from "@ascente/shared-types";
import { toDateInput } from "../../utils/dateHelpers.js";

interface Props {
  entry: Entry | null;
  clients: Client[];
  onSave: (draft: Omit<Entry, "id" | "person" | "addedBy">) => void;
  onClose: () => void;
}

export function EntryForm({ entry, clients, onSave, onClose }: Props) {
  const today = toDateInput(new Date());
  const isPerTask = (clientId: string) => {
    const c = clients.find((cl) => cl.id === clientId);
    return c?.type === "per_task_fixed";
  };

  const [clientId, setClientId] = useState(entry?.clientId ?? "");
  const [task, setTask] = useState(entry?.task ?? "");
  const [hours, setHours] = useState(entry ? String(entry.hours) : "");
  const [taskCharge, setTaskCharge] = useState(entry ? String(entry.taskCharge) : "");
  const [date, setDate] = useState(entry?.date ?? today);

  const handleSave = () => {
    if (!clientId || !task) return;
    onSave({
      clientId,
      task,
      hours: parseFloat(hours) || 0,
      taskCharge: parseFloat(taskCharge) || 0,
      date,
      createdOn: entry?.createdOn,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-title">{entry ? "Edit Entry" : "Add Entry"}</h3>

        <div className="form-row">
          <div className="form-group">
            <label className="inp-label">Client</label>
            <select className="sel" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="inp-label">Date</label>
            <input type="date" className="inp" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="inp-label">Task Description</label>
          <input className="inp" placeholder="What was done?" value={task} onChange={(e) => setTask(e.target.value)} />
        </div>

        <div className="form-row">
          {!isPerTask(clientId) && (
            <div className="form-group">
              <label className="inp-label">Hours</label>
              <input
                type="number"
                className="inp"
                placeholder="0.0"
                step="0.25"
                min="0"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
              />
            </div>
          )}
          {isPerTask(clientId) && (
            <>
              {/* `hours` doubles as a quantity for per_task_fixed clients —
                  calcTaskCharges bills (hours || 1) × taskCharge, so this
                  is the only way to bill more than one occurrence. */}
              <div className="form-group">
                <label className="inp-label">Count</label>
                <input
                  type="number"
                  className="inp"
                  placeholder="1"
                  step="1"
                  min="0"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="inp-label">Task Charge ($)</label>
                <input
                  type="number"
                  className="inp"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  value={taskCharge}
                  onChange={(e) => setTaskCharge(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={!clientId || !task}>
            {entry ? "Save Changes" : "Add Entry"}
          </button>
        </div>
      </div>
    </div>
  );
}
